// Ground-truth storage-pressure signal: a write that actually failed with
// QuotaExceededError. navigator.storage.estimate() can look healthy while
// writes fail (DevTools quota simulation isn't reflected in estimate(), and
// real devices can misreport), so the storage banner treats a recent failed
// write as critical regardless of the reported usage percentage.
//
// Zero imports on purpose — this module is consumed by debugLogger,
// storageManager, and feature modules without creating cycles.
const QUOTA_ERROR_KEY = "artemis_quota_exceeded_at";
const QUOTA_ERROR_TTL_MS = 10 * 60 * 1000;

export function isQuotaError(error) {
  if (!error) return false;
  if (error.name === "QuotaExceededError") return true;
  // Dexie wraps the original DOMException as .inner
  if (error.inner) return isQuotaError(error.inner);
  return typeof error.message === "string" && /quota/i.test(error.message);
}

export function reportQuotaExceeded() {
  try {
    sessionStorage.setItem(QUOTA_ERROR_KEY, String(Date.now()));
  } catch {
    // sessionStorage itself may be failing under the same pressure.
  }
}

// Convenience for catch blocks: flags quota errors, passes everything through.
export function reportIfQuotaError(error) {
  if (isQuotaError(error)) reportQuotaExceeded();
  return error;
}

export function hasRecentQuotaError(now = Date.now()) {
  try {
    const at = Number(sessionStorage.getItem(QUOTA_ERROR_KEY));
    return Number.isFinite(at) && at > 0 && now - at < QUOTA_ERROR_TTL_MS;
  } catch {
    return false;
  }
}

export function clearQuotaError() {
  try {
    sessionStorage.removeItem(QUOTA_ERROR_KEY);
  } catch {
    // Nothing to clear if sessionStorage is unavailable.
  }
}

// Recovery must be proven the same way pressure was: by an actual write.
// estimate() is as untrustworthy for recovery as it is for pressure, so a
// tiny Cache Storage write is attempted; success means the quota pressure is
// gone, a quota failure refreshes the pressure window.
const PROBE_CACHE = "artemis-quota-probe";

export async function probeQuotaRecovered() {
  try {
    const cache = await caches.open(PROBE_CACHE);
    await cache.put("/artemis-quota-probe", new Response("ok"));
    await caches.delete(PROBE_CACHE);
    return true;
  } catch (error) {
    if (isQuotaError(error)) {
      reportQuotaExceeded();
      return false;
    }
    // Cache API unavailable or failed for an unrelated reason: recovery can't
    // be verified — don't hold the banner hostage; the percent-based status
    // still applies and the TTL backstops.
    return true;
  }
}
